import React, { RefObject, useEffect, useRef, useState } from 'react';
import {
  convertSecondsToTimeString,
  getPlayerTimeFromAbsoluteTimePure
} from '@goldcast/player-config/src/utils/timeUtils';
import { zoomLevelStore } from './store/zoomLevel';
import { MIN_SEGMENT_WIDTH } from './timeUtils';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { getClipTotalDurationInFrames } from '@/App/remotion/MainPlayer/utils/configUtils';

export default function HoverPoint({ speakerTimelineRef }: { speakerTimelineRef: RefObject<HTMLDivElement> }) {
  const [x, setX] = useState(0);
  const [time, setTime] = useState(0);
  const { clipData, fps } = useClipsContext();
  const [showHoverPoint, setShowHoverPoint] = useState(true);
  const cursorLastPositionRef = useRef<number>(0);

  useEffect(() => {
    const sceneMenuStateListener = EventBus.on(CustomEvents.SceneMenuStateChanged, ({ isSceneMenuOpen }) => {
      setShowHoverPoint(!isSceneMenuOpen);
    });

    const newTimelineEntityDraggingStateListener = EventBus.on(
      CustomEvents.NewTimelineEntityDraggingStateChanged,
      ({ isDraggingTimelineEntity }) => {
        setShowHoverPoint(!isDraggingTimelineEntity);
      }
    );

    return () => {
      EventBus.off(CustomEvents.SceneMenuStateChanged, sceneMenuStateListener);
      EventBus.off(CustomEvents.NewTimelineEntityDraggingStateChanged, newTimelineEntityDraggingStateListener);
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e?: MouseEvent) => {
      const isOverTooltip = e?.target && (e.target as Element).closest?.('[data-tooltip-overlay]');
      if (isOverTooltip) {
        setShowHoverPoint(false);
        return;
      }
      if (e && e.buttons === 0) {
        setShowHoverPoint(true);
      }

      const zoomLevel = zoomLevelStore.getSnapshot().zoomLevel;
      const rect = speakerTimelineRef.current?.getBoundingClientRect();
      const playerConfig = clipData.asset_metadata.player_config;
      if (!playerConfig || !rect) {
        return;
      }

      const clientX = e?.clientX || cursorLastPositionRef.current;
      cursorLastPositionRef.current = clientX;

      const segmentPositionedTime = (clientX - rect.left) / zoomLevel / fps;
      const relativeTime = getPlayerTimeFromAbsoluteTimePure({
        absoluteTime: segmentPositionedTime,
        clipData: clipData,
        minSegmentWidth: MIN_SEGMENT_WIDTH,
        zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      });

      setX(Math.max(0, Math.min(clientX - rect.left, rect.right - rect.left)) / zoomLevel);
      const totalDurationInFrames = getClipTotalDurationInFrames(clipData);
      setTime(Math.max(0, Math.min(relativeTime, totalDurationInFrames / fps)));
    };

    document.addEventListener('mousemove', handleMouseMove);
    document?.addEventListener('wheel', handleMouseMove);
    handleMouseMove();
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document?.removeEventListener('wheel', handleMouseMove);
    };
  }, [
    clipData,
    showHoverPoint,
    clipData.asset_metadata.deletes,
    clipData.asset_metadata.end,
    clipData.asset_metadata.player_config,
    speakerTimelineRef,
    fps
  ]);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (e.target && speakerTimelineRef.current?.contains(e.target as Node)) {
        setShowHoverPoint(false);
      }
    };
    const handleMouseUp = (e: MouseEvent) => {
      const isOverTooltip = e.target && (e.target as Element).closest?.('[data-tooltip-overlay]');
      if (!isOverTooltip) {
        setShowHoverPoint(true);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [speakerTimelineRef]);

  if (!showHoverPoint) {
    return null;
  }

  // TODO @HiteshChoudhary0098 need to make this fixed positioned so that scroll its visibility
  return (
    <div
      className="pointer-events-none absolute top-0 z-20 hidden h-full w-px bg-slate-600 group-hover/speaker-timeline-container:block"
      style={{
        left: `calc(${x}px * var(--timeline-zoom))`
      }}
    >
      <div
        className="timeline-speaker-label sticky left-0 top-2.5 w-fit -translate-x-1/2 -translate-y-full rounded-sm bg-slate-600 px-1 py-[3px] font-sans text-white"
        data-testid="timeline-hover-time"
      >
        {convertSecondsToTimeString(time, 'HH:mm:ss')}
      </div>
    </div>
  );
}
